import { formatCredits } from '../../../hooks/useCreditPricing';
import { usePipelineEstimate, type EcomKind } from '../../credits/pipelineCost';

// Estimativa do backend (custo de provedor por etapa) para o card do estilo.
function PipelineEstimateLabel({ kind, steps }: { kind: EcomKind; steps: any[] }) {
  const total = usePipelineEstimate(kind, steps);
  if (total == null) return <span />;
  return <span className="font-semibold text-slate-500" title="Estimativa: cada etapa é cobrada pelo uso real quando termina.">≈ {formatCredits(total)} crédito{total !== 1 ? 's' : ''}</span>;
}

export interface PickerPipeline { id: string; name: string; description?: string; isDefault?: boolean; steps: any[] }

// Passo 2: o "estilo" (pipeline) em cards que mostram o que sai dele, em vez de um <select> só com nome.
export function PipelinePicker({ pipelines, selectedId, onSelect, stepChip, estimateKind, onEdit, onCreate }: {
  pipelines: PickerPipeline[]; selectedId: string; onSelect: (id: string) => void;
  stepChip: (step: any) => { icon: string; label: string };
  estimateKind: EcomKind;
  onEdit: (id: string) => void;
  onCreate: () => void;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {pipelines.map(p => {
        const active = p.id === selectedId;
        return (
          <div key={p.id} role="button" tabIndex={0} onClick={() => onSelect(p.id)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(p.id); } }}
            className={`cursor-pointer rounded-xl border-2 p-3 transition ${active ? 'border-indigo-500 bg-indigo-50/50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className={`truncate text-sm font-bold ${active ? 'text-indigo-800' : 'text-slate-800'}`}>{p.name}</p>
                {p.isDefault && <span className="mt-0.5 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">Modelo do sistema</span>}
              </div>
              <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${active ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'}`}>
                {active && <span className="h-2 w-2 rounded-full bg-white" />}
              </span>
            </div>
            {p.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{p.description}</p>}
            <div className="mt-2 flex flex-wrap gap-1">
              {(p.steps || []).map((s, i) => {
                const chip = stepChip(s);
                return (
                  <span key={s.id || i} className="inline-flex items-center gap-1 rounded-md bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200">
                    <span>{chip.icon}</span>{chip.label}
                  </span>
                );
              })}
              {!(p.steps || []).length && <span className="text-[11px] text-amber-700">Sem etapas — edite antes de usar.</span>}
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px]">
              <PipelineEstimateLabel kind={estimateKind} steps={p.steps || []} />
              <button type="button" onClick={e => { e.stopPropagation(); onEdit(p.id); }} className="font-semibold text-indigo-600 hover:underline">
                {p.isDefault ? 'Ver etapas' : 'Editar etapas'}
              </button>
            </div>
          </div>
        );
      })}
      <button type="button" onClick={onCreate}
        className="flex min-h-[7rem] flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 p-3 text-sm font-semibold text-slate-500 hover:border-indigo-400 hover:text-indigo-600">
        <span className="text-xl">+</span>
        Criar meu estilo
      </button>
    </div>
  );
}
